import _ from 'lodash';

import { RESOLUTION_QUERY_TOLERANCE_DPPX } from './constants';

// One MediaQueryList for the whole app: it matches the current density and fires `change` when the
// density leaves it, after which the store re-arms on the new value.
//
// Known limits:
// - Safari page zoom changes devicePixelRatio only from the release with WebKit 303397 (Nov 2025);
//   earlier Safari never fires, so pages keep their first density.
// - `resolution` in dppx needs Safari 16+. Where neither query matches (the iOS 15 WKWebView of the
//   mobile shell) the store stays unarmed and the density is static; iPhone density never changes.
// - Touch and trackpad pinch-zoom do not change devicePixelRatio, so pages keep the last density.
// - Browser zoom changes the density only; it does not refit the page scale.
// - A density below 1 still renders at 1x (getCanvasOutputScale).
const store = { listeners: new Set(), mediaQueryList: null, frameId: null };

export const getDevicePixelRatio = () => window.devicePixelRatio || 1;

// An engine that rounds devicePixelRatio differently from `resolution` misses the exact query; the
// range catches it. Arming a query that does not match would leave a listener that never fires.
const getResolutionMediaQueryList = devicePixelRatio => {
  const exactMediaQueryList = window.matchMedia(`(resolution: ${devicePixelRatio}dppx)`);

  if (exactMediaQueryList.matches) {
    return exactMediaQueryList;
  }

  const rangeMediaQueryList = window.matchMedia(
    `(min-resolution: ${devicePixelRatio - RESOLUTION_QUERY_TOLERANCE_DPPX}dppx) and ` +
      `(max-resolution: ${devicePixelRatio + RESOLUTION_QUERY_TOLERANCE_DPPX}dppx)`,
  );

  return rangeMediaQueryList.matches ? rangeMediaQueryList : null;
};

const armResolutionListener = onChange => {
  const mediaQueryList = _.isFunction(window.matchMedia)
    ? getResolutionMediaQueryList(getDevicePixelRatio())
    : null;

  if (!_.isFunction(mediaQueryList?.addEventListener)) {
    return;
  }

  mediaQueryList.addEventListener('change', onChange);
  store.mediaQueryList = mediaQueryList;
};

const disarmResolutionListener = onChange => {
  store.mediaQueryList?.removeEventListener('change', onChange);
  store.mediaQueryList = null;
};

const notifyListeners = () => {
  store.frameId = null;
  store.listeners.forEach(listener => listener());
};

// A zoom burst fires one `change` per step; listeners hear about it once per frame.
const onResolutionChange = () => {
  disarmResolutionListener(onResolutionChange);
  armResolutionListener(onResolutionChange);

  if (_.isNull(store.frameId)) {
    store.frameId = window.requestAnimationFrame(notifyListeners);
  }
};

export const subscribeToDevicePixelRatio = listener => {
  if (_.isEmpty(store.listeners)) {
    armResolutionListener(onResolutionChange);
  }

  store.listeners.add(listener);

  return () => {
    store.listeners.delete(listener);

    if (!_.isEmpty(store.listeners)) {
      return;
    }

    disarmResolutionListener(onResolutionChange);

    if (!_.isNull(store.frameId)) {
      window.cancelAnimationFrame(store.frameId);
      store.frameId = null;
    }
  };
};
